/**
 * UberEatsRepository — Uber Eats store↔shop mapping management.
 *
 * Talks to the Netlise POS sync API (API Gateway, x-api-key auth) rather than
 * the Amplify/Cognito APIs the rest of the dashboard uses — the mapping
 * endpoints live alongside the Uber Eats webhook/order lambdas.
 *
 *   GET    /ubereats/mapping?shopId=…      → { mappings: [...] }
 *   PUT    /ubereats/mapping               → { mapping }
 *   DELETE /ubereats/mapping?uberStoreId=… → { deleted }
 */

const POS_API_URL = 'https://8cwdlrhppk.execute-api.ap-southeast-1.amazonaws.com/v1';
const POS_API_KEY = 'HRLRCy4bWJ3KrKvZY6NTy3lupIwqNVBH81FNMQZM';

export interface UberStoreMapping {
  uberStoreId: string;
  shopId: string;
  enabled: boolean;
  /** 'provisioned' | 'not_provisioned' | 'uber_error_<code>' */
  provisionStatus?: string;
  updatedAt?: string;
}

async function request(path: string, init?: RequestInit): Promise<any> {
  const res = await fetch(`${POS_API_URL}${path}`, {
    ...init,
    headers: {
      'x-api-key': POS_API_KEY,
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // `message` carries the specific reason; `error` is the generic status text.
    throw new Error(data.message || data.error || `Request failed (${res.status})`);
  }
  return data;
}

const UberEatsRepository = {
  /** Mapping currently configured for a shop (empty array = not connected). */
  async getMapping(shopId: string): Promise<UberStoreMapping[]> {
    const data = await request(`/ubereats/mapping?shopId=${encodeURIComponent(shopId)}`);
    return data.mappings ?? [];
  },

  /** Create/update the mapping and (best-effort) provision the store on Uber. */
  async saveMapping(shopId: string, uberStoreId: string, enabled: boolean): Promise<UberStoreMapping> {
    const data = await request('/ubereats/mapping', {
      method: 'PUT',
      body: JSON.stringify({ shopId, uberStoreId, enabled }),
    });
    return data.mapping;
  },

  async deleteMapping(uberStoreId: string): Promise<void> {
    await request(`/ubereats/mapping?uberStoreId=${encodeURIComponent(uberStoreId)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Publish the shop's POS catalog to every mapped Uber store (Menu API).
   * Menu item ids = POS pids, so incoming order lines match exactly.
   */
  async publishMenu(shopId: string): Promise<{itemsPublished: number; skipped: {pid: string; reason: string}[]}> {
    return request('/ubereats/menu', {
      method: 'POST',
      body: JSON.stringify({shopId}),
    });
  },

  /**
   * Merchant-consent (authorization-code) URL for connecting this shop's Uber
   * store. Generated SERVER-SIDE: the backend signs the shopId into `state`
   * (HMAC) so the OAuth completion endpoint can trust it — a client-built
   * state could claim an arbitrary merchant's shop.
   */
  async getConnectUrl(shopId: string): Promise<string> {
    const data = await request(
      `/ubereats/mapping?connect=1&shopId=${encodeURIComponent(shopId)}`
    );
    if (!data.url) throw new Error('Could not generate the Uber connect link.');
    return data.url;
  },
};

export default UberEatsRepository;
